import { useParams, Link, Navigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Seo } from "@/components/seo/Seo";
import { SITE_PAGES } from "@/data/sitePages";

const Paragraph = ({ text }) => {
  // Render lines that start with "- " as bullets, everything else as <p>.
  const lines = text.split("\n");
  const out = [];
  let bullets = [];
  const flushBullets = () => {
    if (bullets.length) {
      out.push(
        <ul key={`u-${out.length}`} className="list-disc pl-5 space-y-1.5 text-neutral-700 leading-relaxed">
          {bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      );
      bullets = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (line.startsWith("- ")) {
      bullets.push(line.slice(2));
    } else if (line) {
      flushBullets();
      out.push(<p key={`p-${out.length}`} className="text-neutral-700 leading-relaxed">{line}</p>);
    }
  }
  flushBullets();
  return <div className="space-y-3">{out}</div>;
};

// SEO keyword hints per legal/company page slug
const SLUG_KEYWORDS = {
  about: "about Discount Fillers, wholesale supplier aesthetic medicine, B2B clinic supplier, Korea aesthetic distributor",
  "why-wholesale": "why buy wholesale fillers, wholesale vs retail aesthetic, clinic wholesale benefits, bulk aesthetic pricing",
  quality: "authentic Korean fillers, lot traceability, cold-chain shipping, aesthetic quality control, verified batch numbers",
  press: "Discount Fillers press, wholesale aesthetic news, industry coverage",
  careers: "aesthetic medicine careers, work at Discount Fillers, wholesale supplier jobs",
  shipping: "aesthetic filler shipping worldwide, DHL cold-chain, temperature-controlled aesthetic shipping, wholesale delivery",
  returns: "wholesale aesthetic returns policy, filler refund, clinic order returns",
  faq: "wholesale filler FAQ, aesthetic supply questions, clinic sourcing FAQ",
  contact: "contact Discount Fillers, wholesale aesthetic contact, B2B clinic support",
  privacy: "Discount Fillers privacy policy, aesthetic wholesale privacy",
  terms: "Discount Fillers terms of service, wholesale aesthetic terms, B2B clinic agreement",
  cookies: "Discount Fillers cookie policy",
  refunds: "wholesale aesthetic refund policy, clinic order refund",
  moq: "MOQ aesthetic wholesale, minimum order clinic supplies, MOQ 5 units policy",
};

export const SitePage = () => {
  const { slug } = useParams();
  const page = SITE_PAGES[slug];
  if (!page) return <Navigate to="/" replace />;

  const description = page.intro
    ? page.intro.slice(0, 155).replace(/\s+\S*$/, "") + "…"
    : `${page.title} — Discount Fillers wholesale aesthetic supplies.`;
  const keywords = SLUG_KEYWORDS[slug];

  return (
    <>
      <Seo
        title={page.title}
        description={description}
        keywords={keywords}
        path={`/${slug}`}
      />
      <Header />
      <main className="pt-28 md:pt-32 pb-24">
        <div className="max-w-[1100px] mx-auto px-6">
          {/* Breadcrumb */}
          <nav
            className="flex items-center gap-2 text-xs text-neutral-500 tracking-wide mb-10"
            data-testid="site-page-breadcrumb"
          >
            <Link to="/" className="hover:text-black uppercase tracking-[0.2em]">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="uppercase tracking-[0.2em]">{page.eyebrow}</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-black uppercase tracking-[0.2em] font-medium truncate">{page.title}</span>
          </nav>

          {/* Header */}
          <header className="mb-14 max-w-3xl">
            <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 font-medium mb-4">
              {page.eyebrow}
            </div>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02] mb-6"
              data-testid="site-page-title"
            >
              {page.title}
            </h1>
            {page.intro && (
              <p className="text-lg text-neutral-700 font-light leading-relaxed">{page.intro}</p>
            )}
          </header>

          {/* Sections */}
          <div className="grid gap-x-16 gap-y-14 md:grid-cols-12" data-testid="site-page-sections">
            {page.sections.map((s, i) => (
              <section key={i} className="md:col-span-10 md:col-start-2">
                <h2 className="font-heading text-2xl font-black tracking-tight mb-3">
                  {s.heading}
                </h2>
                <Paragraph text={s.body} />
              </section>
            ))}
          </div>

          {/* CTA back to home */}
          <div className="mt-20 pt-10 border-t border-neutral-200 flex items-center justify-between">
            <Link
              to="/"
              className="text-sm font-medium border-b border-black pb-1 hover:gap-3 inline-flex items-center gap-2"
            >
              ← Back to shop
            </Link>
            <span className="text-[11px] tracking-[0.2em] uppercase text-neutral-500">
              © {new Date().getFullYear()} Discount Fillers
            </span>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};
